import React, { useEffect, useState } from "react";
import styles from "./fba-edit-listing-drawer.module.css";
import { FbaListing, FbaListingPatch, useFbaListingContext } from "@/contexts/FbaListingContext";
import { ProductCondition } from "@boxem/core-api-schema";
import { useInventoryContext } from "@/contexts/InventoryContext";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import Button from "@/components/forms/Button/Button";
import IconInfo from "@/components/icons/IconInfo";
import Autocomplete from "@/components/forms/Autocomplete/Autocomplete";
import InputNumber from "@/components/forms/InputNumber/InputNumber";
import Select from "@/components/forms/Select/Select";
import Textarea from "@/components/forms/Textarea/Textarea";
import TextInput from "@/components/forms/TextInput/TextInput";
import { ListPriceOption } from "@/app/fba/utils";
import { productConditionToString, categoryProbablyRequiresExpirationDate, generateListPriceOptions, relcalculateListPriceHelpText } from "@/app/fba/utils";
import Flex from "@/components/Flex/Flex";
import Divider from "@/components/Divider/Divider";
import DatePicker from "@/components/forms/DatePicker";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/Modal/Modal";

export type EditFbaListingItemForm = {
  asin: string;
  sku: string;
  condition: ProductCondition;
  conditionNotes?: string;
  buyCost?: number;
  price: ListPriceOption;
  expirationDate?: string;
  datePurchased?: string;
  supplier?: string;
};

type FbaEditListingFormProps = {
  listing: FbaListing;
  onClose: () => void;
};

const FbaEditListingForm = ({
  listing,
  onClose,
}: FbaEditListingFormProps) => {
  const { editListingInList, addSupplier } = useFbaListingContext();
  const { suppliers, getOffersForASIN, allSellerSkus } = useInventoryContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [invalid, setInvalid] = useState<boolean>(false);
  const [offersLoading, setOffersLoading] = useState<boolean>(true);
  const [listPriceOptions, setListPriceOptions] = useState<ListPriceOption[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<string[]>([]);

  const [listPriceHelpText, setListPriceHelpText] = useState<string>("");
  const [listPriceInvalidText, setListPriceInvalidText] = useState<string | null>(null);
  const [datePurchasedInvalidText, setDatePurchasedInvalidText] = useState<string | null>(null);
  const [skuInvalidText, setSkuInvalidText] = useState<string | null>(null);
  const [expirationDateInvalidText, setExpirationDateInvalidText] = useState<string | null>(null);

  const [form, setForm] = useState<EditFbaListingItemForm>({
    asin: "",
    sku: "",
    condition: ProductCondition.New,
    conditionNotes: "",
    buyCost: null,
    price: null,
    expirationDate: null,
    datePurchased: null,
    supplier: null,
  });

  const handleUpdateListing = () => {
    setLoading(true);

    const patch: FbaListingPatch = {
      sku: form.sku ?? undefined,
      priceUSD: form.price?.price?.toString() ?? undefined,
      condition: form.condition ?? undefined,
      conditionNotes: form.conditionNotes ?? undefined,
      buyCost: form.buyCost?.toString() ?? undefined,
      datePurchased: form.datePurchased ?? undefined,
      expirationDate: form.expirationDate ?? undefined,
      supplier: form.supplier ?? undefined,
    };

    //Modify listing
    editListingInList(listing.sku, patch);

    //Add suppler
    if (!!form.supplier) addSupplier(form.supplier);

    onClose();
    setLoading(false);
  };

  useEffect(() => {
    {
      if (!form.price?.price) {
        setListPriceInvalidText("List price is required");
      } else {
        setListPriceInvalidText(null);  
      }
    }

    {
      if (!form.sku || form.sku.trim().length < 1) {
        setSkuInvalidText("SKU is required!");
      } else if (form.sku.length > 40) {
        setSkuInvalidText("SKU must be 40 characters or less.");
      } else if (!/^[\x20-\x7E]*$/.test(form.sku)) {
        setSkuInvalidText("SKU is invalid - contains invalid character(s).");
      } else if (form.sku !== listing.sku && allSellerSkus.has(form.sku)) {
        setSkuInvalidText("SKU is already in use!");
      } else {
        setSkuInvalidText(null);
      }
    }

    {
      if (!!form.datePurchased) {
        if (new Date(form.datePurchased) > new Date()) {
          setDatePurchasedInvalidText("If provided, date purchased must be in the past.");
        } else {
          setDatePurchasedInvalidText(null);
        }
      } else {
        setDatePurchasedInvalidText(null);
      }
    }

    {
      const expirationDateRequired = categoryProbablyRequiresExpirationDate(listing.category);
      if (!!form.expirationDate) {
        const nintetyDaysInFuture = new Date();
        nintetyDaysInFuture.setDate(nintetyDaysInFuture.getDate() + 90);
        if (new Date(form.expirationDate) < nintetyDaysInFuture) {
          setExpirationDateInvalidText("If provided, expiration date must be at least 90 days in the future.");
        } else {
          setExpirationDateInvalidText(null);
        }
      } else {
        if (expirationDateRequired) {
          setExpirationDateInvalidText(`Items in the category '${listing.category}' require an expiration date.`);
        } else {
          setExpirationDateInvalidText(null);
        }
      }
    }
  }, [form]);

  useEffect(() => {
    setInvalid(!!(skuInvalidText || expirationDateInvalidText || datePurchasedInvalidText || listPriceInvalidText));
  }, [skuInvalidText, expirationDateInvalidText, datePurchasedInvalidText, listPriceInvalidText]);

  useEffect(() => {
    if (form.price?.price && !offersLoading && listPriceOptions && listPriceOptions.length > 0) {
      setListPriceHelpText(relcalculateListPriceHelpText(offersLoading, parseFloat(form.price?.price), listPriceOptions));
    }
  }, [form, offersLoading, listPriceOptions]);

  useEffect(() => {
    if (listing && Array.isArray(suppliers)) {
      const existingListPriceOption: ListPriceOption = { price: listing.priceUSD.toString() };
      setForm({
        ...form,
        price: listing.priceUSD ? existingListPriceOption : null,
        condition: listing.condition,
        conditionNotes: listing.conditionNotes,
        sku: listing.sku,
        expirationDate: listing.expirationDate,
        datePurchased: listing.datePurchased,
        buyCost: parseFloat(listing.buyCost) ?? 0,
        supplier: suppliers.find(supplier => supplier.name === listing.supplier)?.name,
      });

      setSupplierOptions(suppliers.map((supplier) => supplier.name));
  
      setOffersLoading(true);
      getOffersForASIN(listing.asin)
        .then(offers => {
          setListPriceOptions([existingListPriceOption, ...generateListPriceOptions(offers)]);
        })
        .finally(() => {
          setOffersLoading(false);
        });
    }
  }, [listing, suppliers]);

  return (
    <Modal
      open={true}
      onOpenChange={() => {}}
      onClose={() => {
        setInvalid(false);
        onClose();
      }}
      closeOnOutsideClick
      className={styles.modal}
    >
      <ModalHeader>
        <Flex align="center">
          <img src={listing.image || "/images/product-placeholder.png"} alt="Product" width="32" height="32" />
          <Tooltip title={listing.name}>
            <span className={`strong ${styles.productTitle}`}>
              {listing.name}
            </span>
          </Tooltip>
        </Flex>
      </ModalHeader>
      <ModalBody className={styles.modalBody}>
        <form className={styles.form}>
          <h1>Pricing & Profit</h1>

          <div className={styles.inputGroup}>
            <label htmlFor="listPrice">
              <Flex gap={1.5} align="center">
                <span>List price:</span>
                <Tooltip
                  title="List price is the amount a potential buyer will see on the listing for this item."
                >
                  <IconInfo style={{ marginLeft: "0.5ch" }} />
                </Tooltip>
              </Flex>
            </label>

            <Flex direction="column" gap={2}>
              <Autocomplete
                prefix="$"
                inputId="listPrice"
                type="number"
                disabled={loading}
                invalid={!!listPriceInvalidText}
                loading={offersLoading}
                placeholder={listPriceOptions.length === 0 ? "Type a list price" : "Type or select a list price"}
                value={form.price}
                onChange={(option) => setForm({...form, price: option })}
                onTextChange={text => setForm({...form, price: { price: text }})}
                options={listPriceOptions}
                optionToDisplayString={option => option.price?.toString()}
                optionRender={(option) => {
                  return (
                    <Flex justify="space-between" style={{width:"100%"}}>
                      <span>${option.price}</span>
                      <Flex gap={2}>
                        {(option.isBuyBox && option.fba) && (
                          <Tooltip title="This is the FBA buy-box for this item">
                            <img src="/images/buy-box.svg" width={24} height={24} alt="buy box" />
                          </Tooltip>
                        )}
                        {option.isAmazon && (
                          <Tooltip title="Amazon is a seller on the listing">
                            <img src="/images/amazon.svg" width={24} height={24} alt="amazon" />
                          </Tooltip>
                        )}
                        <Tooltip title={option.fba ? "Fulfilled by Amazon" : "Fulfilled by Merchant"}>
                          <span className="weak">{option.fba ? "FBA" : "FBM"}</span>
                        </Tooltip>
                      </Flex>
                    </Flex>
                  );
                }}
              />
              {listPriceInvalidText && <p className={`small-text ${styles.invalidText}`}>{listPriceInvalidText}</p>}
            </Flex>
          </div>

          {(!listPriceInvalidText && listPriceHelpText) && (<span className="small-text weak">{listPriceHelpText}</span>)}

          <div className={styles.inputGroup}>
            <label htmlFor="buyCost">
              <Flex gap={1.5} align="center">
                <span>Buy cost:</span>
                <Tooltip
                  title="Buy cost is the total amount you paid PER UNIT of this item. We store this for you for analytics purposes."
                >
                  <IconInfo style={{ marginLeft: "0.5ch" }} />
                </Tooltip>
              </Flex>
            </label>
            <InputNumber
              id="buyCost"
              disabled={loading}
              prefix="$"
              showControls={false}
              centerText={false}
              value={form.buyCost}
              onChange={v => setForm({...form, buyCost: v})}
            />
          </div>

          <Divider />

          <h1>Supplier info</h1>

          <div className={styles.inputGroup}>
            <label htmlFor="supplier">
              Supplier:
            </label>
            <Autocomplete
              inputId="supplier"
              disabled={loading}
              options={supplierOptions}
              placeholder={supplierOptions.length === 0 ? "Type a supplier name" : "Type or select a supplier name"}
              value={form.supplier}
              onChange={v => setForm({...form, supplier: v})}
              onCreateNewOption={v => {
                setSupplierOptions([ ...supplierOptions, v]);
                setForm({...form, supplier: v});
              }}
              showSearchIcon
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="datePurchased">
              Date purchased:
            </label>
            <Flex direction="column" gap={2}>
              <DatePicker
                id="datePurchased"
                disabled={loading}
                value={form.datePurchased ? new Date(form.datePurchased) : null}
                onChange={v => setForm({...form, datePurchased: v ? v.toString() : null})}
              />
              {datePurchasedInvalidText && (<div className={`small-text ${styles.invalidText}`}>{datePurchasedInvalidText}</div>)}
            </Flex>
          </div>

          <Divider />

          <h1>Product details</h1>

          <div className={styles.inputGroup}>
            <label htmlFor="condition">
              Condition:
            </label>
            <Select
              id="condition"
              disabled={loading}
              fullWidth
              options={[
                ProductCondition.New,
                ProductCondition.UsedLikeNew,
                ProductCondition.UsedVeryGood,
                ProductCondition.UsedGood,
                ProductCondition.UsedAcceptable,
              ]}
              optionRender={option => productConditionToString(option)}
              value={form.condition}
              onChange={v => setForm({...form, condition: v})}
            />
          </div>

          {(form.condition !== ProductCondition.New) && (
            <div className={styles.conditionNotes}>
              <div className={styles.conditionNotesLabel}>
                <label htmlFor="conditionNotes">
                  Notes:
                </label>
              </div>
              <Textarea
                inputId="conditionNotes"
                disabled={loading}
                placeholder="(optional)"
                rows={6}
                maxLength={1000}
                value={form.conditionNotes}
                onChange={v => setForm({...form, conditionNotes: v})}
              />
            </div>
          )}

          <div className={styles.inputGroup}>
            <label htmlFor="sku">
              <Flex gap={1.5} align="center">
                <span>SKU:</span>
                <Tooltip
                  title="The SKU is a per-seller unique identifier for a sellable product. Configure your SKU here, and set up rules for automatic SKU generation inside Variable SKU settings."
                >
                  <IconInfo style={{ marginLeft: "0.5ch" }} />
                </Tooltip>
              </Flex>
            </label>
            <Flex direction="column" gap={2}>
              <TextInput
                inputId="sku"
                invalid={!!skuInvalidText}
                disabled={loading}
                value={form.sku}
                onChange={v => setForm({...form, sku: v})} 
              />
              {skuInvalidText && (<div className={`small-text ${styles.invalidText}`}>{skuInvalidText}</div>)}
            </Flex>
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="expirationDate">
              Expiration date:
            </label>
            <Flex direction="column" gap={2}>
              <DatePicker
                id="expirationDate"
                disabled={loading}
                value={form.expirationDate ? new Date(form.expirationDate) : null}
                onChange={v => setForm({...form, expirationDate: v ? v.toString() : null})}
              />
              {expirationDateInvalidText && (<div className={`small-text ${styles.invalidText}`}>{expirationDateInvalidText}</div>)}
            </Flex>
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <Flex justify="space-between" align="center">
          <Button
            type="secondary"
            disabled={loading}
            onClick={onClose}
            size="large"
          >
            Cancel
          </Button>

          <Button
            style={{minWidth:"10rem"}}
            disabled={loading || invalid}
            onClick={handleUpdateListing}
            size="large"
          >
            Update
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

export default FbaEditListingForm;